import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils";
// import { Message } from "@/types/chat";
import { useChatStore } from "@/stores/chatStore";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { MessageBubble } from "./MessageBubble";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Send,
  Trash2,
  Plug,
  CheckCircle2,
  Cloud,
} from "lucide-react";
import { SiJira, SiGithub, SiSlack, SiLinear } from "react-icons/si";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase";

interface ChatInterfaceProps {
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

interface ConnectedIntegration {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  className,
  placeholder = "Ask me anything about your projects...",
  autoFocus = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [_isTyping, _setIsTyping] = useState(false);
  const [connectedIntegrations, setConnectedIntegrations] = useState<
    ConnectedIntegration[]
  >([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    messages,
    isLoading,
    sendMessage,
    clearChat,
    chatState: _chatState,
    streamingMessageId,
  } = useChatStore();

  const {
    isListening,
    transcript,
    startListening: _startListening,
    stopListening: _stopListening,
    isSupported: _isVoiceSupported,
  } = useVoiceRecognition();

  // Fetch connected integrations
  useEffect(() => {
    if (!user || !supabase) {
      return;
    }

    const fetchConnectedIntegrations = async () => {
      try {
        const integrationMap: Record<string, ConnectedIntegration> = {
          jira: {
            id: "jira",
            name: "Jira",
            icon: SiJira,
            color: "text-blue-600",
            bgColor: "bg-blue-50 border-blue-200",
          },
          github: {
            id: "github",
            name: "GitHub",
            icon: SiGithub,
            color: "text-gray-800",
            bgColor: "bg-gray-50 border-gray-200",
          },
          slack: {
            id: "slack",
            name: "Slack",
            icon: SiSlack,
            color: "text-purple-600",
            bgColor: "bg-purple-50 border-purple-200",
          },
          linear: {
            id: "linear",
            name: "Linear",
            icon: SiLinear,
            color: "text-indigo-600",
            bgColor: "bg-indigo-50 border-indigo-200",
          },
          "azure-devops": {
            id: "azure-devops",
            name: "Azure DevOps",
            icon: Cloud,
            color: "text-cyan-600",
            bgColor: "bg-cyan-50 border-cyan-200",
          },
        };

        // Check for connected integrations
        if (!supabase) return;
        const { data, error } = await supabase
          .from("integration_tokens")
          .select("provider")
          .eq("user_id", user.id);

        if (!error && data) {
          const connected = data
            .map((item: { provider: string }) => integrationMap[item.provider])
            .filter(
              (
                item: ConnectedIntegration | undefined,
              ): item is ConnectedIntegration => item !== undefined,
            );
          setConnectedIntegrations(connected);
        }
      } catch (error) {
        console.error("Error fetching connected integrations:", error);
      }
    };

    fetchConnectedIntegrations();

    // Listen for integration changes
    if (!supabase) return;
    const channel = supabase
      .channel("integration-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "integration_tokens",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchConnectedIntegrations();
        },
      )
      .subscribe();

    return () => {
      if (supabase) {
        supabase.removeChannel(channel);
      }
    };
  }, [user]);

  // Auto-focus input if specified
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle voice input
  useEffect(() => {
    if (transcript && !isListening) {
      setInputValue((prev) => prev + (prev ? " " : "") + transcript);
    }
  }, [transcript, isListening]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const messageText = inputValue.trim();
    setInputValue("");
    _setIsTyping(true);

    try {
      await sendMessage(messageText);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      _setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Voice toggle handler - disabled for now
  // const handleVoiceToggle = () => {
  //   if (isListening) {
  //     stopListening();
  //   } else {
  //     startListening();
  //   }
  // };

  const handleCopyMessage = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Card
      className={cn(
        "flex flex-col h-full overflow-hidden border-0 shadow-none",
        className,
      )}
    >
      {/* Minimal Header - Optional, can be removed for full-screen feel */}
      {messages.length > 0 && (
        <>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 pt-4 px-6 flex-shrink-0 border-b">
            <div className="flex items-center space-x-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <MessageSquare className="h-3.5 w-3.5" />
              </div>
              <CardTitle className="text-base font-medium">Maat AI</CardTitle>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={clearChat}
              className="text-muted-foreground hover:text-foreground h-8"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              <span className="text-xs">Clear Chat</span>
            </Button>
          </CardHeader>
        </>
      )}

      {/* Messages Area */}
      <CardContent className="flex-1 p-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="space-y-6">
              {/* Modern Empty State */}
              {messages.length === 0 && !isLoading && (
                <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <div className="mb-6">
                    <h1 className="text-3xl font-semibold mb-2 text-foreground">
                      How can I assist you today?
                    </h1>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto">
                      Ask me anything about your projects, or connect
                      integrations to get more context about your work.
                    </p>
                  </div>

                  {/* Connected Integrations Bar - Above input */}
                  {connectedIntegrations.length > 0 && (
                    <div className="max-w-3xl mx-auto px-4 mb-3 w-full">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            <span className="font-medium">Connected:</span>
                          </div>
                          <TooltipProvider>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {connectedIntegrations.map((integration) => {
                                const Icon = integration.icon;
                                return (
                                  <Tooltip key={integration.id}>
                                    <TooltipTrigger asChild>
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          "cursor-pointer hover:bg-accent transition-colors",
                                          integration.bgColor,
                                          "px-2.5 py-1 gap-1.5",
                                        )}
                                        onClick={() =>
                                          navigate("/integrations")
                                        }
                                      >
                                        <Icon
                                          className={cn(
                                            "h-3.5 w-3.5",
                                            integration.color,
                                          )}
                                        />
                                        <span className="text-xs font-medium">
                                          {integration.name}
                                        </span>
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        Click to manage {integration.name}{" "}
                                        integration
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          </TooltipProvider>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate("/integrations")}
                          className="text-xs h-7 px-2 gap-1.5 shrink-0"
                        >
                          <Plug className="h-3.5 w-3.5" />
                          <span>Manage</span>
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Modern Input Form - Floating design with icons inside */}
                  <div className="max-w-3xl mx-auto px-4 pb-8 w-full">
                    <form onSubmit={handleSubmit} className="relative">
                      <div className="relative flex items-end rounded-2xl border bg-background shadow-sm hover:shadow-md transition-shadow focus-within:shadow-md focus-within:ring-2 focus-within:ring-ring/20">
                        {/* Plus Icon - Left side - Disabled */}
                        {/* <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute left-2 bottom-2 h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                  e.preventDefault();
                                  // Handle attachment/upload - can be implemented later
                                }}
                                disabled
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Attach file</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider> */}

                        {/* Textarea */}
                        <Textarea
                          ref={inputRef}
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder={placeholder}
                          className="min-h-[52px] max-h-[200px] resize-none border-0 bg-transparent pl-4 pr-20 py-3 focus-visible:ring-0 focus-visible:ring-offset-0"
                          disabled={isLoading}
                        />

                        {/* Right side icons */}
                        <div className="absolute right-2 bottom-2 flex items-center gap-1">
                          {/* Voice Input Button - Disabled */}
                          {/* {isVoiceSupported && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleVoiceToggle}
                                    disabled={isLoading}
                                    className={cn(
                                      "h-8 w-8 rounded-lg transition-colors",
                                      isListening
                                        ? "bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400"
                                        : "text-muted-foreground hover:text-foreground",
                                    )}
                                  >
                                    {isListening ? (
                                      <div className="flex items-center gap-0.5">
                                        <div className="h-1 w-1 rounded-full bg-current animate-pulse" />
                                        <Mic className="h-4 w-4" />
                                      </div>
                                    ) : (
                                      <Mic className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{isListening ? "Stop listening" : "Voice input"}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )} */}

                          {/* Send Button */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="submit"
                                  size="icon"
                                  disabled={!inputValue.trim() || isLoading}
                                  className={cn(
                                    "h-8 w-8 rounded-lg transition-all",
                                    inputValue.trim() && !isLoading
                                      ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                                      : "bg-muted text-muted-foreground cursor-not-allowed",
                                  )}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Send message</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  onCopy={handleCopyMessage}
                  isStreaming={message.id === streamingMessageId}
                />
              ))}

              {/* Modern Typing Indicator */}
              {isLoading && !streamingMessageId && (
                <div className="flex items-start gap-4 animate-in fade-in-0 slide-in-from-bottom-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted shrink-0">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex space-x-1.5 pt-2">
                    <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.3s]" />
                    <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.15s]" />
                    <div className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </ScrollArea>
      </CardContent>

      {/* Modern Floating Input Bar - Only shown when there are messages */}
      {messages.length > 0 && (
        <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="max-w-3xl mx-auto px-4 py-4">
            {/* Connected Integrations Bar - Above input */}
            {connectedIntegrations.length > 0 && (
              <div className="flex items-center justify-between mb-3 w-full">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    <span className="font-medium">Connected:</span>
                  </div>
                  <TooltipProvider>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {connectedIntegrations.map((integration) => {
                        const Icon = integration.icon;
                        return (
                          <Tooltip key={integration.id}>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={cn(
                                  "cursor-pointer hover:bg-accent transition-colors",
                                  integration.bgColor,
                                  "px-2.5 py-1 gap-1.5",
                                )}
                                onClick={() => navigate("/integrations")}
                              >
                                <Icon
                                  className={cn(
                                    "h-3.5 w-3.5",
                                    integration.color,
                                  )}
                                />
                                <span className="text-xs font-medium">
                                  {integration.name}
                                </span>
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Click to manage {integration.name} integration
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </TooltipProvider>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/integrations")}
                  className="text-xs h-7 px-2 gap-1.5 shrink-0"
                >
                  <Plug className="h-3.5 w-3.5" />
                  <span>Manage</span>
                </Button>
              </div>
            )}

            {/* Modern Input Form - Floating design with icons inside */}
            <form onSubmit={handleSubmit} className="relative">
              <div className="relative flex items-end rounded-2xl border bg-background shadow-sm hover:shadow-md transition-shadow focus-within:shadow-md focus-within:ring-2 focus-within:ring-ring/20">
                {/* Plus Icon - Left side - Disabled */}
                {/* <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute left-2 bottom-2 h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          // Handle attachment/upload - can be implemented later
                        }}
                        disabled
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Attach file</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> */}

                {/* Textarea */}
                <Textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="min-h-[52px] max-h-[200px] resize-none border-0 bg-transparent pl-4 pr-20 py-3 focus-visible:ring-0 focus-visible:ring-offset-0"
                  disabled={isLoading}
                />

                {/* Right side icons */}
                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  {/* Voice Input Button - Disabled */}
                  {/* {isVoiceSupported && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={handleVoiceToggle}
                            disabled={isLoading}
                            className={cn(
                              "h-8 w-8 rounded-lg transition-colors",
                              isListening
                                ? "bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400"
                                : "text-muted-foreground hover:text-foreground",
                            )}
                          >
                            {isListening ? (
                              <div className="flex items-center gap-0.5">
                                <div className="h-1 w-1 rounded-full bg-current animate-pulse" />
                                <Mic className="h-4 w-4" />
                              </div>
                            ) : (
                              <Mic className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{isListening ? "Stop listening" : "Voice input"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )} */}

                  {/* Send Button */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="submit"
                          size="icon"
                          disabled={!inputValue.trim() || isLoading}
                          className={cn(
                            "h-8 w-8 rounded-lg transition-all",
                            inputValue.trim() && !isLoading
                              ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                              : "bg-muted text-muted-foreground cursor-not-allowed",
                          )}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Send message</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ChatInterface;
